import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SERVICES = [
  "Chimney Sweeping ($249 Special)",
  "Chimney Repair",
  "Chimney Crown",
  "Chimney Cap",
  "Chimney Flashing",
  "Tuckpointing",
  "Other / Not Sure",
];

const EMPTY = { name: "", phone: "", service: "", preferred_date: "", message: "" };

export function LeadForm({ variant = "quote", defaultService = "" }) {
  const isBooking = variant === "booking";
  const prefix = isBooking ? "book" : "quote";
  const [form, setForm] = useState({ ...EMPTY, service: defaultService });
  const [loading, setLoading] = useState(false);
  const [photo, setPhoto] = useState(null);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || (!isBooking && !form.service)) {
      toast.error("Please fill in your name, phone number, and service needed.");
      return;
    }
    setLoading(true);
    try {
      let attachment_path = null;
      if (photo) {
        const fd = new FormData();
        fd.append("file", photo);
        const up = await axios.post(`${API}/leads/photo`, fd);
        attachment_path = up.data.path;
      }
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        service: isBooking ? SERVICES[0] : form.service,
        source: isBooking ? "book_now" : "quote_request",
        preferred_date: form.preferred_date || null,
        message: form.message || null,
        attachment_path,
      });
      toast.success(
        isBooking
          ? "Booked! We'll call you shortly to confirm your $249 sweep."
          : "Thanks! We'll call you back shortly."
      );
      setForm(EMPTY);
      setPhoto(null);
    } catch (err) {
      toast.error("Something went wrong. Please call us at 971-255-3874.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid={`${prefix}-form`}>
      <div className="space-y-2">
        <Label htmlFor={`${prefix}-name`}>Name</Label>
        <Input
          id={`${prefix}-name`}
          data-testid={`${prefix}-name-input`}
          placeholder="Your full name"
          value={form.name}
          onChange={set("name")}
          autoComplete="name"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${prefix}-phone`}>Phone Number</Label>
        <Input
          id={`${prefix}-phone`}
          data-testid={`${prefix}-phone-input`}
          type="tel"
          placeholder="(503) 555-0123"
          value={form.phone}
          onChange={set("phone")}
          autoComplete="tel"
        />
      </div>
      {isBooking ? (
        <div className="space-y-2">
          <Label htmlFor={`${prefix}-date`}>Preferred Date (optional)</Label>
          <Input
            id={`${prefix}-date`}
            data-testid={`${prefix}-date-input`}
            type="date"
            value={form.preferred_date}
            onChange={set("preferred_date")}
          />
        </div>
      ) : (
        <div className="space-y-2">
          <Label>Service Needed</Label>
          <Select
            value={form.service}
            onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}
          >
            <SelectTrigger data-testid={`${prefix}-service-select`}>
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {SERVICES.map((s) => (
                <SelectItem key={s} value={s} data-testid={`${prefix}-service-option-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor={`${prefix}-photo`}>Photo of Your Chimney (optional)</Label>
        <Input
          id={`${prefix}-photo`}
          data-testid={`${prefix}-photo-input`}
          type="file"
          accept="image/*"
          onChange={(e) => setPhoto(e.target.files?.[0] || null)}
          className="cursor-pointer file:mr-3 file:font-semibold file:text-primary"
        />
        {photo && (
          <p className="text-xs text-muted-foreground" data-testid={`${prefix}-photo-name`}>
            Attached: {photo.name}
          </p>
        )}
      </div>
      <Button
        type="submit"
        disabled={loading}
        data-testid={`${prefix}-submit-button`}
        className="btn-press w-full bg-primary hover:bg-[#A93A24] text-white font-bold text-base h-12 rounded-md"
      >
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : isBooking ? (
          "Book My $249 Sweep"
        ) : (
          "Request My Free Quote"
        )}
      </Button>
      <p className="text-xs text-muted-foreground text-center">
        No spam. We'll call you back within one business day.
      </p>
    </form>
  );
}
